import { EApplicantType, ECreditApplicationStatus, EDecision, EExternalStepStatus } from '@if/core/types'
import { copyMessage } from '@if/core/utils'
import CopyAllIcon from '@mui/icons-material/CopyAll'
import DashboardOutlined from '@mui/icons-material/DashboardOutlined'
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined'
import { Button, Grid, Tooltip, Typography } from '@mui/material'
import { useGetIncomesHistory } from '@src/data/api/credit-application-history/credit-application-history-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import IconButtonWithTooltip from '../../../components/IconButtonWithTooltip'
import {
  useGetFlinksIncomesForApplicant,
  useGetFlinksIncomesForCoApplicant,
  useHasPendingFraudAlert,
} from '@src/data/selectors/credit-selectors'
import { CreditApplication, RequiredExternalStep, User } from '../../../data/types'
import FlinksHistoryDialog from './FlinksHistoryDialog'

interface FlinksActionsSectionProps {
  applicantType: EApplicantType
  handleSaveBankAccountRequest: (applicant: EApplicantType) => void
  handleResetBankAccountRequest: (applicant: EApplicantType) => void
  requiredExternalStep: RequiredExternalStep | undefined
  user: User | null
  creditApplication: CreditApplication
  editDisabled: boolean
  worksheetIsApproved: boolean
  creditApplicationId: string
  confirmJobIncomeTaskCompleted: boolean
  showHistoryDialog?: boolean
}

const FlinksActionsSection = ({
  applicantType: applicantType,
  handleSaveBankAccountRequest,
  handleResetBankAccountRequest,
  requiredExternalStep,
  user,
  creditApplication,
  editDisabled,
  worksheetIsApproved,
  creditApplicationId,
  confirmJobIncomeTaskCompleted,
  showHistoryDialog = true,
}: FlinksActionsSectionProps) => {
  const { t } = useTranslation()
  const INCOME_FLINKS_DIALOG = 'OpenIncomeFlinksDialog'
  const [currentOpenDialog, setCurrentOpenDialog] = useState<string>('')

  const [, isLoadingHistory] = useGetIncomesHistory(
    creditApplicationId,
    creditApplication.financingProgramId,
    currentOpenDialog === INCOME_FLINKS_DIALOG,
  )
  const externalReportedIncomesForApplicant = useGetFlinksIncomesForApplicant()
  const externalReportedIncomesForCoApplicant = useGetFlinksIncomesForCoApplicant()
  const hasPendingFraudAlert = useHasPendingFraudAlert()

  const status = requiredExternalStep?.status

  const applicationIsDraft = creditApplication?.status === ECreditApplicationStatus.Draft

  const externalReportedIncome = () => {
    return applicantType === EApplicantType.Applicant
      ? externalReportedIncomesForApplicant
      : externalReportedIncomesForCoApplicant
  }

  const closeDialogs = useCallback(() => {
    setCurrentOpenDialog('')
  }, [])

  const flinksButtonIsDisabled = (): boolean =>
    !user?.rights.canEditCreditApp ||
    applicationIsDraft ||
    creditApplication?.finalCreditDecision?.decision !== EDecision.Approved ||
    editDisabled ||
    confirmJobIncomeTaskCompleted

  const getFlinksButtonLabel = () => {
    return status ? t('incomeSection.resendLink') : 'FLINKS'
  }

  const copyFlinksLinkIsDisabled =
    flinksButtonIsDisabled() || worksheetIsApproved || status === EExternalStepStatus.Completed

  return (
    <>
      <Grid container direction="row" alignItems="center" columnSpacing={1}>
        <Grid>
          <Tooltip title={hasPendingFraudAlert ? t('editCreditApplication.income.confirmIncomesBlockedByFraudAlert') : ''}>
            <span>
              <Button
                sx={{ mr: 1 }}
                variant="outlined"
                onClick={() => handleSaveBankAccountRequest(applicantType)}
                disabled={
                  flinksButtonIsDisabled() || worksheetIsApproved || status === EExternalStepStatus.Completed || hasPendingFraudAlert
                }
              >
                {getFlinksButtonLabel()}
              </Button>
            </span>
          </Tooltip>
        </Grid>

        <Grid>
          <Typography variant="body2" component="div" sx={{ border: '1px solid', borderRadius: 1, p: 0.85, mr: 1 }}>
            {t('incomeSection.flinksStatus')} :{' '}
            {requiredExternalStep?.status
              ? t(`EExternalStepStatus.${requiredExternalStep.status}`, { ns: 'enum' })
              : t('EExternalStepStatus.notStarted', { ns: 'enum' })}
          </Typography>
        </Grid>

        <Grid>
          <Tooltip title={hasPendingFraudAlert ? t('editCreditApplication.income.confirmIncomesBlockedByFraudAlert') : ''}>
            <span>
              <Button
                sx={{ mr: 1 }}
                variant="outlined"
                onClick={() => handleResetBankAccountRequest(applicantType)}
                disabled={status !== EExternalStepStatus.Completed || hasPendingFraudAlert}
              >
                {t('incomeSection.reset')}
              </Button>
            </span>
          </Tooltip>
        </Grid>

        <Grid>
          <IconButtonWithTooltip
            tooltip={
              copyFlinksLinkIsDisabled
                ? t('common.hoverCopyFlinksLinkDisabled')
                : t('common.hoverCopyFlinksLink')
            }
            icon={<CopyAllIcon />}
            onClick={() => {
              const url = requiredExternalStep?.extraProperties.bankAccountRequestLink
              if (url) {
                copyMessage(url).catch(reportErrorToConsole)
              }
            }}
            disabled={copyFlinksLinkIsDisabled}
          />
        </Grid>
        {requiredExternalStep?.extraProperties?.thirdPartyRequestId && (
          <Grid>
            <IconButtonWithTooltip
              tooltip={t('incomeSection.flinksDashboard')}
              icon={<DashboardOutlined fontSize="medium" />}
              onClick={() => {
                window.open(
                  `https://dashboard.flinks.com/en/534f3619-12c9-4cc3-a07e-c162239a1121/insights/requests/${requiredExternalStep.extraProperties.thirdPartyRequestId}`,
                  '_blank',
                  'noreferrer',
                )
              }}
            />
          </Grid>
        )}
        {externalReportedIncome() && (
          <Grid>
            <IconButtonWithTooltip
              tooltip={t('incomeSection.incomeFlinkHistory')}
              icon={<NewReleasesOutlinedIcon fontSize="medium" />}
              onClick={() => {
                setCurrentOpenDialog(INCOME_FLINKS_DIALOG)
              }}
            />
          </Grid>
        )}
      </Grid>
      {showHistoryDialog && externalReportedIncome() && (
        <FlinksHistoryDialog
          open={currentOpenDialog === INCOME_FLINKS_DIALOG}
          onClose={closeDialogs}
          isLoadingHistory={isLoadingHistory}
          flinksData={externalReportedIncome()!}
        />
      )}
    </>
  )
}

export default FlinksActionsSection
